<template>
  <div
    id="integrationSetting"
    class="mx-5"
  >
    <div class="mt-2 d-flex justify-space-between">
      <div class="d-flex">
        <v-img
          :max-width="60"
          contain
          :src="require(`@/assets/conectiontab/neto.svg`)"
          class="mx-3 mt-1"
        />
        <h2
          style="line-height:85px;"
          class="mr-3"
        >
          Neto
        </h2>
      </div>
      <v-col cols="1">
        <v-btn
          v-track="'close-mcc-drawer'"
          class="text-right mt-3"
          icon
          @click.stop="
            $store.commit('ui/drawer.right.toggle')
          "
        >
          <v-icon
            style="background-color:transparent"
            medium
          >
            mdi-close
          </v-icon>
        </v-btn>
      </v-col>
    </div>
    <div class="mx-3">
      <h2> {{ editingStoreId ? 'Edit' : '' }} Integration Settings</h2>
      <p class="my-4">
        For a step-by-step guide on how to create an integration between Maropost and Neto, refer to
        <a
          v-track="'mcc-help-center-link'"
          href="https://galaxy.maropost.com/s/article/Integration-with-Neto-By-Maropost"
          target="_blank"
        >
          Integration with Neto.
        </a>
      </p>

      <div class="mr-5">
        <div class="pt-3 pb-1 px-3 bg-grey mr-2 body-2">
          <span class="font-weight-light">Maropost will be able to:</span>
          <p>
            View your Neto customers and their purchases.
            <a
              v-track="'mcc-help-center-link'"
              href="https://galaxy.maropost.com/s/article/Integration-with-Neto-By-Maropost"
              target="_blank"
            >
              Learn More
            </a>
          </p>
        </div>
      </div>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
      >
        <div class="d-flex justify-space-between mt-0">
          <v-text-field
            v-model="payload.nick_name"
            label="Shop Name *"
            outlined
            class="mt-3 pt-5"
            :rules="[v => !!v || 'Shop Name is required']"
          />
          <span class="mt-5 ml-1">
            <v-tooltip
              top
              color="rgba(0, 0, 0, 0.9)"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-track="'mcc-name-info'"
                  icon
                  small
                  class="mt-5 pt-1 px-0 pb-0"
                  v-on="on"
                >
                  <v-icon>
                    mdi-information-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>Add a suitable Shop name for your Neto integration.</span>
            </v-tooltip>
          </span>
        </div>
        <div class="d-flex justify-space-between mt-0">
          <v-text-field
            v-model="payload.neto_domain"
            label="Shop URL *"
            outlined
            class="mt-3"
            :disabled="editStore"
            :rules="[v => !!v || 'Shop Url is required']"
          />
          <span class="mt-5 ml-1">
            <v-tooltip
              top
              color="rgba(0, 0, 0, 0.9)"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-track="'mcc-shop-url-info'"
                  icon
                  small
                  class="mt-5 pt-1 px-0 pb-0"
                  v-on="on"
                >
                  <v-icon>
                    mdi-information-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>Add your store URL</span>
            </v-tooltip>
          </span>
        </div>

        <div
          class="d-flex justify-space-between mt-3"
          @click="addListener"
        >
          <v-autocomplete
            v-track="'mcc-store-maropost-list-field'"
            v-model="payload.list_id"
            label="Integrate with Maropost List *"
            :items="listNames"
            item-text="name"
            item-value="id"
            outlined
            :search-input.sync="search"
            :loading="dataLoader"
            :rules="[v => !!v || 'List type is required']"
            required
            attach
            clearable
            class="custom-selectbox"
            @click:clear="clearItems"
            @change="setListName"
          >
          <template #append-item>
            <create-list-option @open-list="contactDialog = !contactDialog"/>
          </template>
          </v-autocomplete>

          <span class="mt-3 ml-1">
            <v-tooltip
              top
              color="rgba(0, 0, 0, 0.9)"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-track="'mcc-store-info'"
                  icon
                  small
                  class="m-0 p-0"
                  v-on="on"
                >
                  <v-icon>
                    mdi-information-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>All Neto contact details will be imported to this<br>list and synced to your Marketing Cloud account.</span>
            </v-tooltip>
          </span>
        </div>
        <v-card
          class="px-4 mt-4 py-2 mb-8">
          <div class="abandoned-trigger-time-div">
            <div class="my-2 d-flex">
              <h4 class="mr-1">
                Abandoned Cart Delay
              </h4>
              <v-tooltip
                top
                color="rgba(0, 0, 0, 0.9)"
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-track="'mcc-shop-url-info'"
                    icon
                    small
                    v-on="on"
                  >
                    <v-icon size="20">
                      mdi-information-outline
                    </v-icon>
                  </v-btn>
                </template>
                <span>
                  Choose a time from 0 minutes to 6 hours to send the first email after receiving the abandoned cart notification
                </span>
              </v-tooltip>
            </div>
            <v-row>
              <v-col class="pb-0">
                <v-divider class="pb-4" />
                <v-autocomplete
                  v-track="'neto-abandoned-hours'"
                  :items="[0,1,2,3,4,5,6]"
                  v-model="abandonedTriggerTime.hrs"
                  label="Hours"
                  outlined
                  required
                  :item-color="constants.status.recurring.color"
                  class="custom-selectbox mr-2"
                  dense
                  light
                  :menu-props="{maxHeight:150}"
                  @change="checkAndSetHrsAttr"
                />
              </v-col>
              <v-col class="pb-0">
                <v-divider class="pb-4" />
                <v-autocomplete
                  v-track="'neto-abandoned-mins'"
                  v-model="abandonedTriggerTime.mins"
                  :items="[0,15,30,45]"
                  item-text="name"
                  item-value="name"
                  label="Minutes"
                  outlined
                  required
                  :item-color="constants.status.recurring.color"
                  class="custom-selectbox mr-2"
                  dense
                  light
                  :disabled="abandonedTriggerTime.hrs == 6"
                  :menu-props="{maxHeight:150}"
                />
              </v-col>
            </v-row>
            <v-divider class="mt-0 pb-2" />
          </div>
          </v-card>

        <v-checkbox
          v-track="'mcc-store-sync-historical-data'"
          v-if="firstConnection"
          v-model="payload.sync_old_data"
          :color="constants.status.recurring.color"
          height="5px"
        >
          <div
            slot="label"
            class="mr-5"
          >
            Sync historical data
          </div>
        </v-checkbox>
          <v-divider class="text-primary mt-2 pb-4 mr-5 pr-2" />
          <IntegrationsCustomFieldMapping
            sourceHeading="Neto Source"
            targetHeading="Marketing Cloud Target"
            v-model="mappedFields"
            :sourceFields="netoFields"
            :payload="payload"
            integrationType="neto"
            ref="integrationCustomFields"
            :mappingLimit="30"
            tooltipIntegrationName="Neto"
            @update-payload="updatePayload"
          />
        <v-btn
          v-if="firstConnection"
          v-track="'connect-mcc-store-button'"
          :class="{
            'mt-5 mb-5 connect-neto-store': true,
            'theme--dark': payload.neto_domain && payload.list_id
          }"
          :loading="isConnecting"
          @click="checkConnectorAppToken()"
          :disabled="!payload.neto_domain || !payload.list_id"
        >
          Connect
        </v-btn>
        <v-btn
          v-track="'update-mcc-store-button'"
          v-if="editStore"
          :class="{
            'mt-5 mb-5 connect-neto-store': true,
            'theme--dark': payload.neto_domain && payload.list_id
          }"
          :loading="isConnecting"
          @click="updateStore()"
          :disabled="!payload.neto_domain || !payload.list_id"
        >
          Save
        </v-btn>
      </v-form>
    </div>
      <!-- Create new contact list drawer -->
    <v-dialog v-model="contactDialog" width="700">
      <create-list isbg-white is-dialog @close-drawer="closeDrawer" />
    </v-dialog>
  </div>
</template>

<script>
  import { $integrations, $connectorApp, $lists, $crms } from '@/services'
  import CreateListOption from '@/components/shared/CreateListOption'
  import IntegrationsCustomFieldMapping from "@/components/shared/IntegrationsCustomFieldMapping.vue"
  import cloneDeep from 'lodash/cloneDeep'
  import constants from '@/constants'
  import { getSplitKey } from '@/splitClient';

  export default {
    name: 'NetoIntegrationDrawer',
    components:{
      CreateList : () => getSplitKey(constants.SPLIT_IO_KEYS.isUIDAccount) ?  import('@/views/lists/lists/UIDCreateList.vue') : import('@/views/lists/lists/CreateList.vue') ,
      CreateListOption,
      IntegrationsCustomFieldMapping
    },
    data () {
      return {
        payload: {
          sync_old_data: true,
        },
        valid: true,
        maro_list: [],
        enableScroll: true,
        page: 0,
        selectorClass: '',
        search: null,
        isSearching: false,
        dataLoader: false,
        listNames: [],
        count: null,
        totalCount: 0,
        editingStoreId: null,
        isConnecting: false,
        contactDialog:false,
        marketingFields: [],
        netoFields: [],
        mappedFields: [
          { neto: '', maro: '' }
        ],
        initialListId: "",
        abandonedTriggerTime: {
          hrs: 0,
          mins: 30
        },
        constants: constants,
      }
    },
    computed: {
      firstConnection () {
        return this.$store.getters['ui/drawer'].right.firstConnection
      },
      editStore () {
        return this.$store.getters['ui/drawer'].right.editStore
      },
    },

    watch: {
      search (val) {
        if (val) {
          if (this.isLoading) return
          this.isLoading = true
          this.isSearching = true
          this.dataLoader = this.constants.status.recurring.color
          $lists.getList({
            params: {
              name: val,
            },
          }).then(response => {
            const responseData = response.data.data.lists
            responseData.forEach(element => {
              const index = this.listNames.findIndex(item => item.id === element.id)
              if (index < 0) this.listNames.push(element)
            })
          })
          .finally(() => {
            this.isLoading = false
            this.dataLoader = false
          })
        } else {
          this.isSearching = false
        }
      }
    },

    mounted () {
      this.getNetoCustomFields();
      if (this.$store.getters['ui/drawer'].right.storeId) {
        this.editingStoreId = this.$store.getters['ui/drawer'].right.storeId
        this.getStoreDetail(this.editingStoreId)
      }
      this.getMoreItems();
    },
    methods: {
      updatePayload(val) {
        this.payload.mapped_fields = val.mapped_fields;
      },
      getNetoCustomFields(){
        $connectorApp.getCustomFields('neto')
        .then(res => {
          this.netoFields = res.data;
        });
      },
      /**
       * Closes drawer and fetches updated contact list of selected integration
       */
      closeDrawer(data) {
        if (data) {
          this.listNames.push(data);
          this.payload.list_id = data.id;
        }
        this.contactDialog = false;
        this.getMoreItems();
      },
      validate () {
        return this.$refs.form.validate()
      },
      clearItems () {
        this.payload.list_id = null
      },
      addListener () {
        this.selectorClass = '.v-autocomplete__content'
        var el = document.querySelector(this.selectorClass)
        /** add scroll event **/
        if (el) { el.addEventListener('scroll', this.onScroll) }
      },
      onScroll ({ target: { scrollTop, clientHeight, scrollHeight } }) {
        if (Math.ceil(scrollTop + clientHeight) >= scrollHeight) {
          if (this.enableScroll) {
            this.getMoreItems()
          }
        }
      },
      getMoreItems () {
        let params = {}
        if (this.maro_list.length > this.listNames) {
          this.listNames = [...this.maro_list]
        }
        this.dataLoader = this.constants.status.recurring.color
        ++this.page
        if (!this.$store.getters['ui/drawer'].right.storeId) {
          params = {
            page: this.page,
            per_page: 10,
            no_folder: true,
            list_type: 'normal',
          }
        }
        $lists.getList({ params }).then(response => {
          const data = response.data.data.lists
          this.totalCount = response.data.data.meta.count
          const dataLength = data.length
          if (dataLength === 0) {
            this.enableScroll = false
          } else {
            this.enableScroll = true
            data.forEach(element => {
              const index = this.listNames.findIndex(item => item.id === element.id)
              if (index < 0) {
                this.listNames.push(element)
              }
            })
          }
        }).finally(() => {
          this.dataLoader = false
        })
      },

      checkConnectorAppToken () {
        this.valid = this.validate();
        if (this.valid) {
          this.isConnecting = true
          if (Object.keys(this.$store.getters['auth/connectorAppData']).length > 0) {
            const currentTime = new Date().getTime()
            const generatedTokenTime = new Date(this.$store.getters['auth/connectorAppData'].generationTime).getTime()
            const timeElapsed = (currentTime - generatedTokenTime) / 1000
            const expiresInTime = this.$store.getters['auth/connectorAppData'].expire
            if (timeElapsed < expiresInTime) {
              this.makeStoreConnection()
            } else {
              this.generateConnectorAuthToken()
            }
          } else {
            this.generateConnectorAuthToken()
          }
        }
      },

      generateConnectorAuthToken () {
        $integrations.getConnectorAuthToken().then(res => {
          const data = res.data
          data.generationTime = new Date()
          this.$store.commit('auth/connectorAppData', data)
          this.checkConnectorAppToken()
        })
      },

      async makeStoreConnection () {
        this.valid = this.validate();
        if (this.valid) {
          await this.$refs.integrationCustomFields.mappedFieldsHash();
          this.payload['abandoned_trigger_time'] = `${this.abandonedTriggerTime.hrs}:${this.abandonedTriggerTime.mins}`
          $connectorApp.createStoreConnection('neto', this.payload)
            .then(res => {
              window.location.href = res.data.redirect_url;
            })
            .finally(() => {
              this.isConnecting = false
            })
        }
      },
      async updateStore () {
        this.valid = this.validate();
        if (this.valid) {
          this.isConnecting = true;
          let editPayload = {};
          await this.$refs.integrationCustomFields.mappedFieldsHash();
          if (this.payload.list_id != undefined) {
             editPayload = {
              list_id: this.payload.list_id,
              mapped_fields: this.payload.mapped_fields,
              neto_domain: this.payload.neto_domain,
              abandoned_trigger_time:  `${this.abandonedTriggerTime.hrs}:${this.abandonedTriggerTime.mins}`,
              nick_name: this.payload.nick_name
            }
            if (this.storeData.crm_lists.length > 0) {
              let crmlist_id = this.storeData.crm_lists.filter(crm_list => {
                return crm_list.list_id == this.initialListId &&
                       crm_list.crm_id == this.storeData.id;
              })
              if(crmlist_id[0]){
                editPayload.crmlist_id = crmlist_id[0].id;
              }
            }
          }
          $connectorApp.updateConnectorStore('neto', editPayload)
          .then(res => {
            this.$store.commit('ui/snackbar', {
              message: 'Store Successfully Updated!',
              type: 'success',
            })
            setTimeout(() => {
              this.$store.commit('ui/drawer.right', {
                component: 'NetoIntegrationDrawer',
                show: false,
                width: '700px',
              })
            }, 1000)
            this.$router.push({ name: 'Neto_index', query: { edit: new Date().getTime() } })
            this.isConnecting = false
          })
          .finally(() => {
            this.isConnecting = false
          })
        }
      },
      getStoreDetail (storeId) {
        $integrations.getStoreDetail('neto', storeId)
          .then(async res => {
            this.storeData = res.data.crm;
            if (res.data.crm.crm_lists.length > 0) {
              this.payload = {
                sync_old_data: true,
                neto_domain: res.data.crm.name,
                list_id: res.data.crm.crm_lists[0].list_id,
                nick_name: res.data.crm.nick_name,
              }
              const index = this.listNames.findIndex(item => item.id === res.data.crm.crm_lists[0].list_id)
              if (index < 0) {
                this.listNames.push({ id: res.data.crm.crm_lists[0].list_id, name: this.$store.getters['ui/drawer'].right.integrationType })
              }
              this.$set(this.payload, 'mapped_fields',  res.data.crm.data);
              this.initialListId = cloneDeep(this.payload.list_id)
              if(res.data.crm.abandoned_trigger_time){
                let time = res.data.crm.abandoned_trigger_time;
                this.abandonedTriggerTime = {
                  hrs: time.hours,
                  mins: time.minutes
                } 
              }
            } else {
              this.payload = {
                sync_old_data: true,
                neto_domain: res.data.crm.name,
              }
            }
          })
      },
      setListName (id) {
        var selectedObj = this.listNames.filter(obj => {
          return obj.id == id
        })
        localStorage.setItem('selected_integration_list_name', selectedObj[0].name)
      },
      checkAndSetHrsAttr(){
          if(this.abandonedTriggerTime.hrs == 6){
            this.abandonedTriggerTime.mins = 0
          }
        }
      }
  }
</script>

<style scoped>
.bg-grey{
  background-color: #F4F4F4;
}
.mt-55{
  margin-top: 55px !important;
}
</style>
